import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import StatsCard from '../../components/ui/StatsCard';
import { expensesService } from '../../services/expensesService';
import { projectService } from '../../services/projectService';
import divisionsService, { extractDivisionCode } from '../../services/divisionsService';
import projectCostService from '../../services/projectCostService';
import { supabase } from '../../lib/supabase';
import useTranslation from '../../hooks/useTranslation';
import paymentRequestService from '../../services/paymentRequestService';
import DocumentsModal from './components/DocumentsModal';
import SearchableSelect from '../../components/ui/SearchableSelect';
import GroupedSelect from '../../components/ui/GroupedSelect';
import PortalDropdown from '../../components/ui/PortalDropdown';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';
import { statusVariant } from '../../utils/statusVariant';
import Badge from '../../components/ui/Badge';
import BulkActionBar from '../../components/ui/BulkActionBar';

// ─── Helpers ─────────────────────────────────────────────────
const fmt = (val) => fmtCAD(val);

const fmtDate = (d, language) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00')?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Libelle d'une DP dans un selecteur ───────────────────────
// Toutes les DP sont selectionnables, approuvees comprises : un sous-traitant
// valide son pourcentage AVANT l'approbation et envoie sa facture APRES. Sans
// cela ces factures n'ont nulle part ou atterrir.
// Le statut est un suffixe TEXTE : une <option> native ne rend aucun balisage,
// le texte est donc la seule pastille possible. 'draft' n'en porte aucun.
const dpOptionLabel = (pr, t, prefix) => {
  const suffix =
    pr?.status === 'approved'  ? t(`${prefix}.dpApproved`)
    : pr?.status === 'submitted' ? t(`${prefix}.dpSubmitted`)
    : null;
  return suffix ? `DP${pr?.number} \u2014 ${suffix}` : `DP${pr?.number}`;
};

// ─── Status Badge ─────────────────────────────────────────────
const STATUS_CONFIG = {
  draft:    { bg: 'bg-gray-100',   text: 'text-gray-600',  hex: '#6B7280', hexBg: '#F3F4F6' },
  approved: { bg: 'bg-green-100',  text: 'text-green-700', hex: '#15803D', hexBg: '#DCFCE7' },
  paid:     { bg: 'bg-blue-100',   text: 'text-blue-700',  hex: '#1D4ED8', hexBg: '#DBEAFE' }
};

const statusLabel = (t, status) => t(`expenses.status.${STATUS_CONFIG?.[status] ? status : 'draft'}`);

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.draft;
  return (
    <Badge variant={statusVariant('expenses', status)}>
      {statusLabel(t, status)}
    </Badge>
  );
};

// ─── Export Helpers ───────────────────────────────────────────
const exportToCSV = (rows, filename, costCodes, t, language) => {
  const c = 'expenses.export.columns';
  const headers = [
    t(`${c}.number`), t(`${c}.date`), t(`${c}.vendor`), t(`${c}.description`), t(`${c}.costItem`),
    t(`${c}.amount`), t(`${c}.tax`), t(`${c}.total`), t(`${c}.status`), t(`${c}.paidBy`), t(`${c}.reference`)
  ];
  const csvRows = rows?.map((exp, i) => {
    const cc = costCodes?.find(c => c?.costCode === exp?.costCode);
    const costItem = exp?.costCode
      ? `${exp?.costCode}${cc?.description ? ' – ' + cc?.description : ''}`
      : exp?.extraId ? t('expenses.table.extraBadge') : '—';
    return [
      (i + 1)?.toString()?.padStart(2, '0'),
      fmtDate(exp?.expenseDate, language),
      exp?.vendor || '—',
      exp?.description || '—',
      costItem,
      (parseFloat(exp?.amount) || 0)?.toFixed(2),
      (parseFloat(exp?.taxAmount) || 0)?.toFixed(2),
      (parseFloat(exp?.totalAmount) || 0)?.toFixed(2),
      exp?.status ? statusLabel(t, exp?.status) : '—',
      exp?.paidBy || '—',
      exp?.referenceNumber || '—'
    ]?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(',');
  });
  const csv = [headers?.map(h => `"${h}"`)?.join(','), ...csvRows]?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a?.click();
  URL.revokeObjectURL(url);
};

const exportToPDF = (rows, filename, title, costCodes, t, language) => {
  const statusColors = {
    draft:    { bg: '#F3F4F6', text: '#6B7280' },
    approved: { bg: '#DCFCE7', text: '#15803D' },
    paid:     { bg: '#DBEAFE', text: '#1D4ED8' }
  };

  const tableRows = rows?.map((exp, i) => {
    const cc = costCodes?.find(c => c?.costCode === exp?.costCode);
    const costItem = exp?.costCode
      ? `${exp?.costCode}${cc?.description ? ' – ' + cc?.description : ''}`
      : exp?.extraId ? t('expenses.table.extraBadge') : '—';
    const st = exp?.status || 'draft';
    const sc = statusColors?.[st] || statusColors?.draft;
    const label = statusLabel(t, st);
    return `
      <tr>
        <td style="padding:6px 8px;font-size:11px;color:#FF6B2C;font-family:monospace;">${(i + 1)?.toString()?.padStart(2, '0')}</td>
        <td style="padding:6px 8px;font-size:11px;color:#6B7280;">${fmtDate(exp?.expenseDate, language)}</td>
        <td style="padding:6px 8px;font-size:11px;font-weight:600;color:#111827;">${exp?.vendor || '—'}</td>
        <td style="padding:6px 8px;font-size:11px;color:#374151;max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${exp?.description || '—'}</td>
        <td style="padding:6px 8px;font-size:11px;color:#374151;">${costItem}</td>
        <td style="padding:6px 8px;font-size:11px;font-weight:600;color:#111827;text-align:right;">${fmt(exp?.totalAmount)}</td>
        <td style="padding:6px 8px;">
          <span style="display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:${sc?.bg};color:${sc?.text};">${label}</span>
        </td>
      </tr>`;
  })?.join('');

  const totalAmount = rows?.reduce((s, e) => s + (parseFloat(e?.totalAmount) || 0), 0);

  const count = rows?.length || 0;
  const countLabel = t(count === 1 ? 'expenses.export.countOne' : 'expenses.export.countMany', { count });
  const generatedLabel = t('expenses.export.generated', {
    date: new Date()?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { month: 'long', day: 'numeric', year: 'numeric' })
  });

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${title}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 24px; background: #fff; color: #111827; }
    .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 2px solid #FF6B2C; }
    .header-title { font-size: 18px; font-weight: 700; color: #2C3E50; }
    .header-sub { font-size: 12px; color: #6B7280; margin-top: 2px; }
    .badge-orange { display: inline-block; padding: 3px 10px; border-radius: 9999px; font-size: 11px; font-weight: 600; background: #FFF0E8; color: #FF6B2C; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    thead tr { background: #F9FAFB; border-bottom: 1px solid #E5E7EB; }
    thead th { padding: 8px; font-size: 11px; font-weight: 600; color: #374151; text-align: left; }
    thead th:nth-child(6) { text-align: right; }
    tbody tr { border-bottom: 1px solid #F3F4F6; }
    tbody tr:hover { background: #FAFAFA; }
    .footer { margin-top: 16px; padding-top: 12px; border-top: 1px solid #E5E7EB; display: flex; justify-content: flex-end; }
    .total-box { background: #F9FAFB; border-radius: 8px; padding: 10px 16px; text-align: right; }
    .total-label { font-size: 11px; color: #6B7280; }
    .total-value { font-size: 16px; font-weight: 700; color: #111827; margin-top: 2px; }
    @media print { body { padding: 12px; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="header-title">${t('expenses.export.reportTitle')}</div>
      <div class="header-sub">${title} &nbsp;·&nbsp; ${countLabel} &nbsp;·&nbsp; ${generatedLabel}</div>
    </div>
    <span class="badge-orange">${t('expenses.export.badge')}</span>
  </div>
  <table>
    <thead>
      <tr>
        <th>${t('expenses.export.columns.number')}</th>
        <th>${t('expenses.export.columns.date')}</th>
        <th>${t('expenses.export.columns.vendor')}</th>
        <th>${t('expenses.export.columns.description')}</th>
        <th>${t('expenses.export.columns.costItem')}</th>
        <th style="text-align:right;">${t('expenses.export.columns.total')}</th>
        <th>${t('expenses.export.columns.status')}</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
  <div class="footer">
    <div class="total-box">
      <div class="total-label">${t('expenses.export.grandTotal')}</div>
      <div class="total-value">${fmt(totalAmount)}</div>
    </div>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win?.document?.write(html);
  win?.document?.close();
  win?.focus();
  setTimeout(() => { win?.print(); }, 400);
};

// ─── Document Icon Button ─────────────────────────────────────
const DocumentIconButton = ({ expense, onClick }) => {
  const { t } = useTranslation();
  const [docCount, setDocCount] = useState(0);
  const [docNames, setDocNames] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef(null);

  useEffect(() => {
    if (!expense?.id) return;
    const storagePath = `expense/${expense?.id}`;
    supabase?.storage
      ?.from('contracts-documents')
      ?.list(storagePath, { limit: 20, sortBy: { column: 'name', order: 'asc' } })
      ?.then(({ data }) => {
        const filtered = (data || [])?.filter(f => f?.name && f?.name !== '.emptyFolderPlaceholder');
        setDocCount(filtered?.length);
        setDocNames(filtered?.map(f => f?.name));
      })
      ?.catch(() => {});
  }, [expense?.id]);

  const handleMouseEnter = () => {
    tooltipTimeout.current = setTimeout(() => setShowTooltip(true), 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(tooltipTimeout?.current);
    setShowTooltip(false);
  };

  const hasDocuments = docCount > 0;

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={onClick}
        className="relative p-1 rounded transition-colors group"
        title={t('expenses.rowActions.documents')}
      >
        <Icon
          name="FileText"
          size={13}
          className={`transition-colors ${hasDocuments ? 'text-orange-500' : 'text-gray-400'} group-hover:text-gray-700`}
        />
        <span
          className={`absolute -top-1 -right-1 text-[10px] rounded-full px-1 leading-tight font-medium ${
            hasDocuments ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-400'
          }`}
          style={{ minWidth: '14px', textAlign: 'center' }}
        >
          {docCount}
        </span>
      </button>
      {showTooltip && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white shadow-md rounded-md px-2 py-1.5 min-w-[120px] max-w-[180px] pointer-events-none">
          {hasDocuments ? (
            <div className="space-y-0.5">
              {docNames?.slice(0, 3)?.map((name, i) => (
                <p key={i} className="text-[10px] text-gray-600 truncate">{name}</p>
              ))}
              {docCount > 3 && (
                <p className="text-[10px] text-gray-400">{t('expenses.rowActions.moreDocuments', { count: docCount - 3 })}</p>
              )}
            </div>
          ) : (
            <p className="text-[10px] text-gray-500">{t('expenses.rowActions.noDocuments')}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Actions Menu (3-dot) — portal-based, never clips inside table overflow ───
const ActionsMenu = ({ expense, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  const items = [
    {
      label: t('expenses.rowActions.edit'),
      icon: 'Pencil',
      onClick: () => onEdit?.(expense),
    },
    {
      label: t('expenses.rowActions.delete'),
      icon: 'Trash2',
      onClick: () => onDelete(expense?.id),
      variant: 'danger',
    },
  ];

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        title={t('expenses.rowActions.actions')}
      >
        <Icon name="MoreVertical" size={15} />
      </button>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        width={160}
      />
    </>
  );
};

// ─── Add / Edit Expense Modal ─────────────────────────────────
const ExpenseModal = ({ projectId, divisions, costCodes, extras, expense, onClose, onSave }) => {
  const { t } = useTranslation();
  // If expense has _fromInbox, treat as new (not edit) but pre-filled
  const isEdit = !!expense && !expense?._fromInbox;

  const [form, setForm] = useState({
    expenseDate: expense?.expenseDate || '',
    vendor: expense?.vendor || '',
    description: expense?.description || '',
    divisionSelection: expense?.divisionCode ? (() => {
      // Try to reconstruct divisionSelection from existing expense
      // We can't know category from divisionCode alone at init, so leave blank
      return '';
    })() : '',divisionCode: expense?.divisionCode || '',costCode: expense?.costCode || '',extraId: expense?.extraId || '',
    amount: expense?.amount !== undefined ? String(expense?.amount) : '',
    tps: expense?.tps !== undefined ? String(expense?.tps) : '0.00',
    tvq: expense?.tvq !== undefined ? String(expense?.tvq) : '0.00',referenceNumber: expense?.referenceNumber || '',status: expense?.status || 'paid',paidBy: expense?.paidBy || '',paymentRequestId: expense?.paymentRequestId || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [paymentRequests, setPaymentRequests] = useState([]);

  // Load project DPs
  useEffect(() => {
    if (!projectId) return;
    paymentRequestService?.getByProjectId(projectId)
      ?.then(data => setPaymentRequests(data || []))
      ?.catch(err => console.error('[ExpenseModal] failed to load DPs:', err));
  }, [projectId]);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleAmountChange = (val) => {
    const amt = parseFloat(val) || 0;
    const calcTps = amt > 0 ? (amt * 0.05)?.toFixed(2) : '0.00';
    const calcTvq = amt > 0 ? (amt * 0.09975)?.toFixed(2) : '0.00';
    setForm(p => ({ ...p, amount: val, tps: calcTps, tvq: calcTvq }));
  };

  const filteredCostCodes = (() => {
    const [selCat, selCode] = (form?.divisionSelection || '')?.split('::');
    if (!selCat) return [];
    if (selCat === 'construction') {
      return costCodes?.filter(c => c?.category === 'construction' && extractDivisionCode(c?.costCode) === selCode);
    }
    // general or admin: single auto-filled code
    return costCodes?.filter(c => c?.category === selCat && c?.costCode === selCode);
  })();

  const amount = parseFloat(form?.amount) || 0;
  const tps = parseFloat(form?.tps) || 0;
  const tvq = parseFloat(form?.tvq) || 0;
  const totalAmount = amount + tps + tvq;

  const handleSave = async () => {
    setError('');
    if (!form?.expenseDate) { setError(t('expenses.validation.dateRequired')); return; }
    if (!form?.vendor?.trim()) { setError(t('expenses.validation.vendorRequired')); return; }
    if (amount <= 0) { setError(t('expenses.validation.amountRequired')); return; }
    if (!form?.paymentRequestId) { setError(t('expenses.validation.paymentRequestRequired')); return; }
    if (!form?.costCode && !form?.extraId) {
      setError(t('expenses.validation.costCodeOrExtraRequired'));
      return;
    }

    setSaving(true);
    try {
      const taxAmount = tps + tvq;
      // Decode divisionSelection to get final cost_code and division_code for payload
      const [selCat, selCode] = (form?.divisionSelection || '')?.split('::');
      const finalDivisionCode = selCat === 'construction' ? selCode : (selCode || form?.divisionCode || null);
      const payload = { ...form, projectId, amount, tps, tvq, totalAmount, taxAmount, divisionCode: finalDivisionCode, costCode: form?.costCode || null };
      let saved;
      if (isEdit) {
        saved = await expensesService?.update(expense?.id, payload);
      } else {
        saved = await expensesService?.create(payload);
      }

      // If from inbox, mark inbox item as processed
      if (expense?._fromInbox && expense?._inboxItemId) {
        try {
          await supabase?.from('invoice_inbox')?.update({ status: 'processed' })?.eq('id', expense?._inboxItemId);
        } catch (err) {
          console.error('Failed to mark inbox item as processed:', err);
        }
      }

      onSave(saved);
      onClose();
    } catch (err) {
      setError(err?.message || t('expenses.validation.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              {isEdit ? t('expenses.modal.editTitle') : t('expenses.modal.addTitle')}
            </h2>
            {expense?._fromInbox && (
              <p className="text-[10px] text-blue-500 mt-0.5 flex items-center gap-1">
                <Icon name="Receipt" size={10} />
                {t('expenses.modal.prefilledFromInbox')}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600">
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4 expense-modal-form">
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {/* Row 1: Date + Vendor */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('expenses.modal.dateLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form?.expenseDate}
                onChange={e => setField('expenseDate', e?.target?.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('expenses.modal.vendorLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.vendor}
                onChange={e => setField('vendor', e?.target?.value)}
                placeholder={t('expenses.modal.vendorPlaceholder')}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {/* Row 2: Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('expenses.modal.descriptionLabel')}</label>
            <input
              type="text"
              value={form?.description}
              onChange={e => setField('description', e?.target?.value)}
              placeholder={t('expenses.modal.descriptionPlaceholder')}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Payment Request (DP) — mandatory */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('expenses.modal.dpLabel')} <span className="text-red-500">*</span>
            </label>
            <select
              value={form?.paymentRequestId || ''}
              onChange={e => setField('paymentRequestId', e?.target?.value)}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
            >
              <option value="">{t('expenses.modal.dpNoneOption')}</option>
              {(paymentRequests || [])?.map(pr => (
                <option key={pr?.id} value={pr?.id}>
                  {dpOptionLabel(pr, t, 'expenses.modal')}
                </option>
              ))}
            </select>
          </div>

          {/* Row 3: Division + Cost Code */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('expenses.modal.divisionLabel')}</label>
              {(() => {
                const constructionDivCodes = new Set(
                  (costCodes || [])
                    ?.filter(c => c?.category === 'construction')
                    ?.map(c => extractDivisionCode(c?.costCode))
                    ?.filter(Boolean)
                );
                const constructionDivisions = (divisions || [])?.filter(d => constructionDivCodes?.has(d?.code));
                const generalCodes = (costCodes || [])?.filter(c => c?.category === 'general');
                const adminCodes = (costCodes || [])?.filter(c => c?.category === 'admin');
                const groups = [
                  ...(constructionDivisions?.length ? [{
                    label: t('expenses.modal.divisionGroupConstruction'),
                    options: constructionDivisions?.map(d => ({ value: `construction::${d?.code}`, label: `${d?.code} – ${d?.name}` }))
                  }] : []),
                  ...(generalCodes?.length ? [{
                    label: t('expenses.modal.divisionGroupGeneral'),
                    options: generalCodes?.map(c => ({ value: `general::${c?.costCode}`, label: `${c?.costCode} – ${c?.description}` }))
                  }] : []),
                  ...(adminCodes?.length ? [{
                    label: t('expenses.modal.divisionGroupAdmin'),
                    options: adminCodes?.map(c => ({ value: `admin::${c?.costCode}`, label: `${c?.costCode} – ${c?.description}` }))
                  }] : []),
                ];
                return (
                  <GroupedSelect
                    groups={groups}
                    value={form?.divisionSelection}
                    onChange={val => {
                      const [cat, code] = (val || '')?.split('::');
                      if (!val) {
                        setField('divisionSelection', '');
                        setField('divisionCode', '');
                        setField('costCode', '');
                      } else if (cat === 'construction') {
                        setForm(p => ({ ...p, divisionSelection: val, divisionCode: code, costCode: '' }));
                      } else {
                        setForm(p => ({ ...p, divisionSelection: val, divisionCode: code, costCode: code }));
                      }
                    }}
                    placeholder={t('expenses.modal.divisionPlaceholder')}
                  />
                );
              })()}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('expenses.modal.costCodeLabel')}</label>
              {(() => {
                const [selCat] = (form?.divisionSelection || '')?.split('::');
                if (selCat === 'construction') {
                  return (
                    <SearchableSelect
                      options={filteredCostCodes?.map(c => ({ value: c?.costCode, label: `${c?.costCode} – ${c?.description}` }))}
                      value={form?.costCode}
                      onChange={val => setField('costCode', val || '')}
                      placeholder={t('expenses.modal.costCodePlaceholder')}
                    />
                  );
                } else if (selCat === 'general' || selCat === 'admin') {
                  return (
                    <input
                      type="text"
                      readOnly
                      value={form?.costCode || ''}
                      className="w-full text-xs border border-gray-100 rounded-lg px-3 py-2 bg-gray-50 text-gray-600 cursor-default focus:outline-none"
                    />
                  );
                } else {
                  return (
                    <SearchableSelect
                      options={[]}
                      value=""
                      onChange={() => {}}
                      placeholder={t('expenses.modal.costCodePlaceholder')}
                      disabled
                    />
                  );
                }
              })()}
            </div>
          </div>

          {/* Extra / Crédit dropdown */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('expenses.modal.extraLabel')}</label>
            <SearchableSelect
              options={(extras || [])?.map(ex => ({ value: ex?.id, label: `${t('expenses.modal.extraOptionPrefix')} — ${ex?.title}` }))}
              value={form?.extraId}
              onChange={val => setField('extraId', val || '')}
              placeholder={t('expenses.modal.extraPlaceholder')}
              allowNone
            />
          </div>

          {/* Row 4: Amount + TPS / TVQ + Total */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('expenses.modal.amountLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form?.amount}
                onChange={e => handleAmountChange(e?.target?.value)}
                placeholder="0.00"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('expenses.modal.tpsLabel')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form?.tps}
                readOnly
                placeholder="0.00"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-500 cursor-default focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('expenses.modal.tvqLabel')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form?.tvq}
                readOnly
                placeholder="0.00"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-500 cursor-default focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="bg-gray-50 rounded-lg px-4 py-2 flex flex-col justify-center">
              <span className="text-xs font-medium text-gray-600">{t('expenses.modal.totalLabel')}</span>
              <span className="text-sm font-bold text-gray-900 mt-0.5">{fmt(totalAmount)}</span>
            </div>
          </div>

          {/* Row 5: Reference + Paid by */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('expenses.modal.referenceLabel')}</label>
              <input
                type="text"
                value={form?.referenceNumber}
                onChange={e => setField('referenceNumber', e?.target?.value)}
                placeholder={t('expenses.modal.referencePlaceholder')}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('expenses.modal.paidByLabel')}</label>
              <SearchableSelect
                options={[
                  { value: 'visa', label: 'Visa' },
                  { value: 'cheque', label: t('expenses.modal.paidByCheque') },
                ]}
                value={form?.paidBy}
                onChange={val => setField('paidBy', val || '')}
                placeholder={t('expenses.modal.paidByPlaceholder')}
                allowNone
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? t('common.saving') : (isEdit ? t('common.save') : t('expenses.modal.addExpense'))}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Expenses Table ───────────────────────────────────────────
const ExpensesTable = ({ expenses, onEdit, onDelete, costCodes, paymentRequests, dpSort, onToggleDpSort, onOpenDocuments, selectedIds, onToggleSelect, onToggleAll }) => {
  const { t, language } = useTranslation();
  const allSelected = expenses?.length > 0 && selectedIds?.size === expenses?.length;
  const someSelected = selectedIds?.size > 0 && !allSelected;

  // Build a DP-number lookup and optionally sort rows by it
  const prMap = useMemo(() => {
    const m = {};
    (paymentRequests || [])?.forEach(pr => { m[pr?.id] = pr?.number ?? 0; });
    return m;
  }, [paymentRequests]);

  const sortedExpenses = useMemo(() => {
    if (!dpSort) return expenses;
    return [...expenses]?.sort((a, b) => {
      const na = prMap[a?.paymentRequestId] ?? 0;
      const nb = prMap[b?.paymentRequestId] ?? 0;
      return dpSort === 'asc' ? na - nb : nb - na;
    });
  }, [expenses, dpSort, prMap]);

  if (expenses?.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-12 text-center">
          <Icon name="Receipt" size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500">{t('expenses.empty.noExpenses')}</p>
          <p className="text-xs text-gray-400 mt-1">{t('expenses.empty.clickToAdd')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
      <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              <th className="px-4 py-3 w-[40px]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={() => onToggleAll(expenses)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                />
              </th>
              <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3 w-[40px]">#</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('expenses.table.date')}</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('expenses.table.vendor')}</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('expenses.table.description')}</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('expenses.table.costItem')}</th>
              <th
                className="text-left text-xs font-semibold text-gray-700 px-4 py-3 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                onClick={onToggleDpSort}
              >
                <span className="inline-flex items-center gap-1">
                  {t('expenses.table.dp')}
                  {dpSort === 'asc' ? (
                    <Icon name="ChevronUp" size={12} className="text-primary" />
                  ) : dpSort === 'desc' ? (
                    <Icon name="ChevronDown" size={12} className="text-primary" />
                  ) : (
                    <Icon name="ChevronsUpDown" size={12} className="text-gray-400" />
                  )}
                </span>
              </th>
              <th className="text-right text-xs font-semibold text-gray-700 px-4 py-3">{t('expenses.table.amount')}</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">{t('expenses.table.status')}</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3 w-[90px]">{t('expenses.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedExpenses?.map((exp, index) => (
              <tr
                key={exp?.id}
                className={`hover:bg-gray-50/60 transition-colors ${selectedIds?.has(exp?.id) ? 'bg-orange-50/40' : ''}`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds?.has(exp?.id)}
                    onChange={() => onToggleSelect(exp?.id)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-orange-600 font-mono">{(index + 1)?.toString()?.padStart(2, '0')}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-gray-500">{fmtDate(exp?.expenseDate, language)}</span>
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs font-medium text-gray-900">{exp?.vendor || '—'}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs text-gray-600 max-w-[200px] truncate">{exp?.description || '—'}</p>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {exp?.costCode ? (() => {
                      const cc = costCodes?.find(c => c?.costCode === exp?.costCode);
                      return <span className="text-xs font-medium text-gray-900">{exp?.costCode}{cc?.description ? ` – ${cc?.description}` : ''}</span>;
                    })() : <span className="text-xs font-medium text-gray-900">—</span>}
                    {exp?.extraId && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700">
                        {t('expenses.table.extraBadge')}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {(() => {
                    const pr = (paymentRequests || [])?.find(p => p?.id === exp?.paymentRequestId);
                    return pr ? (
                      <span className="text-xs font-medium text-gray-900">DP{pr?.number}</span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-xs font-semibold text-gray-900">{fmt(exp?.totalAmount)}</span>
                  {(parseFloat(exp?.taxAmount) || 0) > 0 && (
                    <p className="text-[10px] text-gray-400">{t('expenses.table.taxPrefix')} {fmt(exp?.taxAmount)}</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={exp?.status} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <DocumentIconButton
                      expense={exp}
                      onClick={() => onOpenDocuments(exp)}
                    />
                    <ActionsMenu
                      expense={exp}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const ExpensesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();

  const initialProject = location?.state?.project;
  // Inbox pre-load: when navigated from Invoice Inbox with an expense document
  const inboxItem = location?.state?.inboxItem || null;
  const fromInbox = location?.state?.fromInbox || false;

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(initialProject || null);
  const [expenses, setExpenses] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [extras, setExtras] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dpSort, setDpSort] = useState(null); // null | 'asc' | 'desc'

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Documents modal state
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [selectedExpenseForDocs, setSelectedExpenseForDocs] = useState(null);

  // Inbox pre-fill banner state
  const [inboxBannerDismissed, setInboxBannerDismissed] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [expData, divData, costData, extrasData, prData] = await Promise.all([
        expensesService?.getByProject(id),
        divisionsService?.getByProjectId(id),
        projectCostService?.getByProjectId ? projectCostService?.getByProjectId(id) : Promise.resolve([]),
        supabase?.from('project_extras')?.select('id, title')?.eq('project_id', id)?.order('created_at', { ascending: false })?.then(({ data }) => data || [])?.catch(() => []),
        paymentRequestService?.getByProjectId(id)?.catch(() => [])
      ]);
      setExpenses(expData || []);
      setDivisions(divData || []);
      setCostCodes(costData || []);
      setExtras(extrasData || []);
      setPaymentRequests(prData || []);
    } catch (err) {
      console.error('[ExpensesPage] fetchData error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchData();
      if (!initialProject) {
        projectService?.getById(id)?.then(data => setProject(data))?.catch(err => console.error(err));
      }
    }
  }, [id]);

  // Auto-open add modal pre-filled with inbox data when arriving from inbox
  useEffect(() => {
    if (fromInbox && inboxItem && !loading) {
      const ext = inboxItem?.extractedData || {};
      const prefilledExpense = {
        expenseDate: ext?.invoiceDate || ext?.date || '',
        vendor: ext?.supplier || '',
        description: inboxItem?.fileName || '',
        amount: ext?.amount ? String(ext?.amount) : '',
        tps: ext?.amount ? (parseFloat(ext?.amount) * 0.05)?.toFixed(2) : '0.00',
        tvq: ext?.amount ? (parseFloat(ext?.amount) * 0.09975)?.toFixed(2) : '0.00',
        referenceNumber: ext?.invoiceNumber || '',
        status: 'draft',
        paidBy: '',
        divisionCode: '',
        costCode: '',
        extraId: '',
        _fromInbox: true,
        _inboxItemId: inboxItem?.id,
        _inboxFilePath: inboxItem?.filePath,
        _inboxFileName: inboxItem?.fileName,
      };
      setEditingExpense(prefilledExpense);
    }
  }, [fromInbox, inboxItem, loading]);

  // KPI calculations
  const kpis = {
    totalExpenses: expenses?.length,
    totalAmount: expenses?.reduce((s, e) => s + (parseFloat(e?.totalAmount) || 0), 0),
    paidAmount: expenses?.filter(e => e?.status === 'paid')?.reduce((s, e) => s + (parseFloat(e?.totalAmount) || 0), 0)
  };

  const handleDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await expensesService?.delete(pendingDeleteId);
      setExpenses(prev => prev?.filter(e => e?.id !== pendingDeleteId));
      setSelectedIds(prev => { const next = new Set(prev); next?.delete(pendingDeleteId); return next; });
    } catch (err) {
      console.error('[ExpensesPage] delete error:', err);
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleSaved = (saved) => {
    if (editingExpense) {
      setExpenses(prev => prev?.map(e => e?.id === saved?.id ? saved : e));
    } else {
      setExpenses(prev => [saved, ...prev]);
    }
    setEditingExpense(null);
  };

  const handleOpenDocuments = (expense) => {
    setSelectedExpenseForDocs(expense);
    setDocumentsModalOpen(true);
  };

  // Filtered rows
  const filteredExpenses = useMemo(() => {
    if (!searchQuery?.trim()) return expenses;
    const q = searchQuery?.toLowerCase()?.trim();
    return expenses?.filter(exp => {
      const vendor = exp?.vendor?.toLowerCase() || '';
      const description = exp?.description?.toLowerCase() || '';
      const costCode = exp?.costCode?.toLowerCase() || exp?.cost_code?.toLowerCase() || '';
      const category = exp?.category?.toLowerCase() || '';
      return vendor?.includes(q) || description?.includes(q) || costCode?.includes(q) || category?.includes(q);
    });
  }, [expenses, searchQuery]);

  // Selection handlers
  const handleToggleSelect = (expId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(expId)) next?.delete(expId);
      else next?.add(expId);
      return next;
    });
  };

  const handleToggleAll = (rows) => {
    const allSelected = rows?.every(r => selectedIds?.has(r?.id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows?.map(r => r?.id)));
    }
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  // Export handlers — toolbar (all filtered rows)
  const projectLabel = project?.projectNumber ? `${project?.projectNumber} – ${project?.name || ''}` : (project?.name || t('expenses.export.projectFallback'));

  const handleExportCSV = () => {
    exportToCSV(filteredExpenses, `expenses_${projectLabel?.replace(/\s+/g, '_')}.csv`, costCodes, t, language);
  };

  const handleExportPDF = () => {
    exportToPDF(filteredExpenses, `expenses_${projectLabel?.replace(/\s+/g, '_')}.pdf`, projectLabel, costCodes, t, language);
  };

  // Export handlers — bulk (selected rows only)
  const handleBulkExportCSV = () => {
    const rows = filteredExpenses?.filter(e => selectedIds?.has(e?.id));
    exportToCSV(rows, `expenses_selected_${projectLabel?.replace(/\s+/g, '_')}.csv`, costCodes, t, language);
  };

  const handleBulkExportPDF = () => {
    const rows = filteredExpenses?.filter(e => selectedIds?.has(e?.id));
    exportToPDF(rows, `expenses_selected_${projectLabel?.replace(/\s+/g, '_')}.pdf`, `${projectLabel} (${t('expenses.export.selectedSuffix')})`, costCodes, t, language);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-[#FAFBFC] pt-0 pb-0 px-0 h-full flex flex-col">
      {/* Inbox pre-load banner */}
      {fromInbox && inboxItem && !inboxBannerDismissed && (
        <div className="flex items-center gap-3 px-4 py-2.5 mb-4 bg-blue-50 border border-blue-100 rounded-xl">
          <Icon name="Receipt" size={14} className="text-blue-500 flex-shrink-0" />
          <p className="text-xs text-blue-700 flex-1">
            <span className="font-semibold">{t('expenses.inbox.bannerLabel')}</span>{' '}
            {inboxItem?.fileName || t('expenses.inbox.documentFallback')}
            {inboxItem?.extractedData?.supplier ? ` · ${inboxItem?.extractedData?.supplier}` : ''}
          </p>
          <button
            onClick={() => setInboxBannerDismissed(true)}
            className="text-blue-400 hover:text-blue-600 transition-colors"
          >
            <Icon name="X" size={13} />
          </button>
        </div>
      )}
      {/* Header Block with KPI Cards */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Receipt" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-[#2C3E50]">
                {t('expenses.title')}
              </h1>
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.projectNumber || t('common.na')}
              </span>
              <span className="text-sm text-primary font-normal">-</span>
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.name || t('common.loading')}
              </span>
            </div>
            <p className="text-xs text-gray-500">{t('expenses.subtitle')}</p>
          </div>
          <div className="flex-1 flex items-center justify-end gap-2">
            <StatsCard
              title={t('expenses.kpi.totalExpenses')}
              value={kpis?.totalExpenses}
              sublabel=""
              iconName="Receipt"
              iconColor="text-blue-500"
              valueColor="text-blue-600"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('expenses.kpi.totalAmount')}
              value={fmt(kpis?.totalAmount)}
              sublabel=""
              iconName="DollarSign"
              iconColor="text-gray-400"
              valueColor="text-gray-900"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('expenses.kpi.paid')}
              value={fmt(kpis?.paidAmount)}
              sublabel=""
              iconName="Banknote"
              iconColor="text-emerald-500"
              valueColor="text-emerald-700"
              titleWeight="font-medium"
            />
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 gap-3">
        {/* Search Box + count */}
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder={t('expenses.toolbar.searchPlaceholder')}
              className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={12} />
              </button>
            )}
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {filteredExpenses?.length} {filteredExpenses?.length !== 1 ? t('expenses.toolbar.expenses') : t('expenses.toolbar.expense')}
          </span>
        </div>

        {/* Export buttons */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleExportCSV}
            disabled={filteredExpenses?.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={t('expenses.toolbar.exportCsvTooltip')}
          >
            <Icon name="FileSpreadsheet" size={13} className="text-green-500" />
            CSV
          </button>
          <button
            onClick={handleExportPDF}
            disabled={filteredExpenses?.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={t('expenses.toolbar.exportPdfTooltip')}
          >
            <Icon name="FileText" size={13} className="text-red-500" />
            PDF
          </button>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm"
        >
          <Icon name="Plus" size={13} />
          {t('expenses.addExpense')}
        </button>
      </div>

      {/* Bulk Action Bar */}
      <BulkActionBar
        count={selectedIds?.size}
        actions={[
          {
            key: 'csv',
            label: t('bulk.exportCsv'),
            icon: 'FileSpreadsheet',
            variant: 'default',
            onClick: handleBulkExportCSV,
          },
          {
            key: 'pdf',
            label: t('bulk.exportPdf'),
            icon: 'FileText',
            variant: 'default',
            onClick: handleBulkExportPDF,
          },
        ]}
        onClear={handleClearSelection}
        clearLabel={t('bulk.clear')}
      />

      {/* Table */}
      <ExpensesTable
        expenses={filteredExpenses}
        onEdit={(exp) => setEditingExpense(exp)}
        onDelete={setPendingDeleteId}
        costCodes={costCodes}
        paymentRequests={paymentRequests}
        dpSort={dpSort}
        onToggleDpSort={() => setDpSort(prev => prev === null ? 'asc' : prev === 'asc' ? 'desc' : null)}
        onOpenDocuments={handleOpenDocuments}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleAll={handleToggleAll}
      />

      {/* Modals */}
      {pendingDeleteId && (
        <ConfirmDialog
          isOpen={!!pendingDeleteId}
          title={t('expenses.delete.title')}
          message={t('expenses.delete.message')}
          confirmVariant="danger"
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          onConfirm={handleDelete}
          onCancel={() => setPendingDeleteId(null)}
        />
      )}
      {(showAddModal || editingExpense) && (
        <ExpenseModal
          projectId={id}
          divisions={divisions}
          costCodes={costCodes}
          extras={extras}
          expense={editingExpense || null}
          onClose={() => { setShowAddModal(false); setEditingExpense(null); }}
          onSave={handleSaved}
        />
      )}

      {/* Documents Modal */}
      <DocumentsModal
        isOpen={documentsModalOpen}
        onClose={() => { setDocumentsModalOpen(false); setSelectedExpenseForDocs(null); }}
        entityId={selectedExpenseForDocs?.id}
        entityType="expense"
        subtitle={selectedExpenseForDocs?.vendor ? `${selectedExpenseForDocs?.vendor}${selectedExpenseForDocs?.description ? ' – ' + selectedExpenseForDocs?.description : ''}` : selectedExpenseForDocs?.description || ''}
        onFilesChanged={() => {}}
      />
    </div>
  );
};

export default ExpensesPage;
